import React, { useState, useEffect, useRef, useMemo } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../AuthContext";

import {
  fetchPracticeQuestions,
  fetchExamQuestions,
  submitUserAnswer,
  checkAnswerExistsInDB,
  submitSecondAttemptAnswer,
  updateUserAnswer,
  updateSecondAttemptAnswer,
  fetchWithAuth,
  fetchSessionAnswers,
  snapshotCompletedExams,
  fetchFinalSnapshot
} from "../utils/api";

import "./ExamPage.css";

const ExamPage = () => {
  const { examId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, token } = useAuth();

  // ---- Flags from router location.state ----
  const selectedCourses = location.state?.selectedCourses || [];
  const examName = location.state?.examName || "Unknown Exam";
  const sessionId = location.state?.sessionId;
  const isResuming = location.state?.isResuming || false;
  const isReviewResume = location.state?.isReviewResume || false;
  const isViewingCompleted = location.state?.isViewingCompleted || false;

  // ---- Base question data ----
  const [questions, setQuestions] = useState([]);
  const [courseHeaders, setCourseHeaders] = useState({});
  const [questionSectionMap, setQuestionSectionMap] = useState({});
  const [scenarioMap, setScenarioMap] = useState({});

  // ---- For storing the user’s existing DB answers (including second_answer) ----
  const [existingAnswers, setExistingAnswers] = useState([]);

  // ---- Page state ----
  const [pageIndex, setPageIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [reviewAnswers, setReviewAnswers] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // ---- Mode toggles ----
  const [isExamStarted, setIsExamStarted] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [isExamMode, setIsExamMode] = useState(false);

  // ---- Second-attempt review mode ----
  const [isReviewMode, setIsReviewMode] = useState(false);
  const [isReviewStarted, setIsReviewStarted] = useState(false);
  const [reviewQuestions, setReviewQuestions] = useState([]);
  const [reviewCurrentIndex, setReviewCurrentIndex] = useState(0);

  // ---- Final results ----
  const [finalScore, setFinalScore] = useState(null);
  const [finalResults, setFinalResults] = useState([]);
  const [courseScores, setCourseScores] = useState({});
  const [managerExplanations, setManagerExplanations] = useState({});
  const [finalSnapshot, setFinalSnapshot] = useState([]);

  // ---- For focusing free-answer text areas
  const textAreaRef = useRef(null);

  // ---- For 'Report an Issue' modal
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [issueDescription, setIssueDescription] = useState("");

  // Make sure these modal functions come BEFORE using them in any return:
  const openReportModal = () => setIsReportModalOpen(true);
  const closeReportModal = () => setIsReportModalOpen(false);

  console.log("DBG: isReviewMode=", isReviewMode, "| isReviewStarted=", isReviewStarted);

  // ---------------------------------------------------------------------------
  // 1) finalize exam session in DB
  // ---------------------------------------------------------------------------
  async function updateExamSessionAsCompleted(sessionId, finalScore, token) {
    try {
      const numericScore = Math.round(finalScore * 100);
      const body = {
        end_time: new Date().toISOString(),
        score: numericScore,
        status: "completed",
        mode: "Completed"
      };
      const response = await fetchWithAuth(`/api/exam_sessions/complete/${sessionId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });
      if (!response.ok) {
        console.error("Failed to update exam session as completed:", response.status);
      }
    } catch (err) {
      console.error("Error updating exam session as completed:", err);
    }
  }

  // ---------------------------------------------------------------------------
  // 2) T/FA indexes
  // ---------------------------------------------------------------------------
  const tfaIndexes = useMemo(() => {
    return questions
      .map((q, idx) => (q.type === "T" || q.type === "FA" ? idx : -1))
      .filter((idx) => idx !== -1);
  }, [questions]);

  const currentMasterIndex =
    tfaIndexes[pageIndex] !== undefined ? tfaIndexes[pageIndex] : 0;
  const currentQuestion = questions[currentMasterIndex] || null;

  const currentCourseId = currentQuestion?.course_id;
  const currentHeaderText = currentCourseId && courseHeaders[currentCourseId];
  const currentSectionText =
    currentQuestion && questionSectionMap[currentQuestion.id];
  const scenarioTexts = currentQuestion ? scenarioMap[currentQuestion.id] || [] : [];

  // ---------------------------------------------------------------------------
  // 3) If isViewingCompleted => redirect
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (isViewingCompleted) {
      navigate("/dashboard/completed-exams", {
        state: { sessionId, userId: user.userId }
      });
    }
  }, [isViewingCompleted, sessionId, user, navigate]);

  // ---------------------------------------------------------------------------
  // 4) If we set isExamMode = true => load exam Q
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (isExamMode) {
      loadExamQuestions();
    }
  }, [isExamMode]);

  // ---------------------------------------------------------------------------
  // (A) Resume in *Review* Mode => we want to load Q, fetch answers, see partial "second_answer"
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (isResuming && isReviewResume) {
      console.log(
        "⏯ Resuming a *review* session, reading second_answer from DB to pick up where user left off"
      );
      setIsReviewMode(true);
      setIsReviewStarted(true); // So we skip the splash

      (async () => {
        setIsLoading(true);
        try {
          // 1) Load exam questions
          await loadExamQuestions(true);
          // 2) Fetch existing answers (including second_answer) from DB
          const fetched = await fetchSessionAnswers(sessionId, user.userId, token);
          setExistingAnswers(Array.isArray(fetched) ? fetched : []);
        } catch (err) {
          console.error("Error in review-resume flow:", err);
        } finally {
          setIsLoading(false);
        }
      })();
    }
  }, [isResuming, isReviewResume, sessionId, token]);

  // ---------------------------------------------------------------------------
  // (B) Resume normal exam => skip practice, force exam mode
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (isResuming && !isReviewResume) {
      console.log("⏯ Resuming normal exam => skip practice => force exam mode");
      setIsExamMode(true);
      setIsExamStarted(true);
    }
  }, [isResuming, isReviewResume]);

  // ---------------------------------------------------------------------------
  // (C) Once exam Q are loaded in normal exam => fetch answers + set last page
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (
      isExamMode &&
      isExamStarted &&
      questions.length > 0 &&
      isResuming &&
      !isReviewMode
    ) {
      handleResumeExam();
    }
  }, [isExamMode, isExamStarted, questions, isResuming, isReviewMode]);

  // ---------------------------------------------------------------------------
  // 5) If we specifically resumed in "Review" mode, parse second_answer, populate reviewAnswers
  // ---------------------------------------------------------------------------
  useEffect(() => {
    // Only do this if resuming a second-attempt (Review) from the DB
    if (!(isResuming && isReviewResume)) return;

    // Also confirm we are indeed in review mode, and "review started"
    if (!isReviewMode || !isReviewStarted) return;
    if (questions.length === 0 || existingAnswers.length === 0) return;

    console.log("⚙ Building second-attempt 'reviewAnswers' from existingAnswers");

    // 1) Build local answers from first attempt
    const tempFirstAttempt = {};
    // 2) Also build local secondAttempt from second_answer
    const tempSecondAttempt = {};

    for (const row of existingAnswers) {
      // row: { question_id, selected_answer, is_correct, second_answer, ... }
      const qId = row.question_id;
      // For normal attempt
      const isNum1st = /^\d+$/.test(row.selected_answer);
      tempFirstAttempt[qId] = isNum1st
        ? parseInt(row.selected_answer, 10)
        : row.selected_answer;

      // For second attempt
      if (row.second_answer) {
        const isNum2nd = /^\d+$/.test(row.second_answer);
        tempSecondAttempt[qId] = isNum2nd
          ? parseInt(row.second_answer, 10)
          : row.second_answer;
      }
    }
    // Now store them
    setAnswers(tempFirstAttempt);
    setReviewAnswers(tempSecondAttempt);

    // 3) Identify T-type questions that were incorrect
    const incorrectT = [];
    for (const row of existingAnswers) {
      if (!row.is_correct) {
        const q = questions.find((qq) => qq.id === row.question_id);
        if (q && q.type === "T") {
          incorrectT.push({
            ...q,
            previous_user_answer: row.selected_answer
          });
        }
      }
    }
    setReviewQuestions(incorrectT);

    // 4) Figure out which second-attempt question was last answered (if any)
    const answered2nd = Object.keys(tempSecondAttempt).map(Number);
    const relevantQids = incorrectT.map((q) => q.id);
    const answeredReview = answered2nd.filter((qid) => relevantQids.includes(qid));

    if (answeredReview.length === 0) {
      setReviewCurrentIndex(0);
      return;
    }
    // Otherwise, find last answered second-attempt Q
    answeredReview.sort((a, b) => a - b);
    const last2nd = answeredReview[answeredReview.length - 1];
    const idx = incorrectT.findIndex((q) => q.id === last2nd);
    setReviewCurrentIndex(idx >= 0 ? idx : 0);
  }, [
    isResuming,
    isReviewResume,
    isReviewMode,
    isReviewStarted,
    questions,
    existingAnswers
  ]);

  // ---------------------------------------------------------------------------
  // 6) Focus text area automatically
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!isReviewMode && currentQuestion?.type === "FA") {
      textAreaRef.current?.focus();
    }
  }, [pageIndex, currentQuestion, isReviewMode]);

  useEffect(() => {
    if (
      isReviewMode &&
      isReviewStarted &&
      reviewQuestions[reviewCurrentIndex]?.type === "FA"
    ) {
      textAreaRef.current?.focus();
    }
  }, [isReviewMode, isReviewStarted, reviewCurrentIndex, reviewQuestions]);

  // ---------------------------------------------------------------------------
  // Helpers for random subsets
  // ---------------------------------------------------------------------------
  const shuffleArray = (arr) => arr.sort(() => Math.random() - 0.5);
  const getRandomSubset = (array, size) => {
    const shuffled = shuffleArray([...array]);
    return shuffled.slice(0, size);
  };

  // ---------------------------------------------------------------------------
  // loadPracticeQuestions
  // ---------------------------------------------------------------------------
  const loadPracticeQuestions = async () => {
    if (!examId || selectedCourses.length === 0) {
      setError("Missing exam or course info.");
      return;
    }
    setIsLoading(true);

    try {
      let allCourseData = [];
      let lastHeaderPerCourse = {};
      let lastSectionPerCourse = {};
      let scenarioTextsPerCourse = {};

      selectedCourses.forEach((courseId) => {
        lastHeaderPerCourse[courseId] = `No header found for ${courseId}`;
        lastSectionPerCourse[courseId] = "";
        scenarioTextsPerCourse[courseId] = [];
      });

      for (const courseId of selectedCourses) {
        const questionData = await fetchPracticeQuestions(examId, courseId, token);
        allCourseData.push(...questionData);
      }

      allCourseData.sort((a, b) => a.id - b.id);

      let finalPracticeQuestions = [];

      for (const q of allCourseData) {
        const cid = q.course_id;
        if (q.type === "H") {
          lastHeaderPerCourse[cid] = q.question;
        } else if (q.type === "S") {
          lastSectionPerCourse[cid] = q.question;
          scenarioTextsPerCourse[cid] = [];
        } else if (q.type === "Q") {
          scenarioTextsPerCourse[cid].push(q.question);
        }
        finalPracticeQuestions.push(q);

        if (q.type === "T") {
          questionSectionMap[q.id] = lastSectionPerCourse[cid] || "";
          scenarioMap[q.id] = [...scenarioTextsPerCourse[cid]];
        }
      }

      const justT = finalPracticeQuestions.filter((qq) => qq.type === "T");
      let groupedByCourse = {};
      for (const fq of justT) {
        if (!groupedByCourse[fq.course_id]) {
          groupedByCourse[fq.course_id] = [];
        }
        groupedByCourse[fq.course_id].push(fq);
      }

      let practiceSetOfT = [];
      for (const courseId of selectedCourses) {
        const Tarray = groupedByCourse[courseId] || [];
        const num = Math.ceil(Tarray.length / 3);
        const subset = getRandomSubset(Tarray, num);
        practiceSetOfT.push(...subset);
      }
      practiceSetOfT.sort((a, b) => a.id - b.id);

      finalPracticeQuestions.sort((a, b) => a.id - b.id);

      setCourseHeaders(lastHeaderPerCourse);
      setQuestionSectionMap(questionSectionMap);
      setScenarioMap(scenarioMap);

      setQuestions(finalPracticeQuestions);
      setError(null);
    } catch (err) {
      console.error("Error fetching practice Qs:", err);
      setError("Failed to load questions.");
    } finally {
      setIsLoading(false);
    }
  };

  // ---------------------------------------------------------------------------
  // loadExamQuestions
  // ---------------------------------------------------------------------------
  const loadExamQuestions = async (skipClearingState = false) => {
    if (!examId || selectedCourses.length === 0) {
      setError("Missing exam or course info.");
      return;
    }
    if (!skipClearingState) setIsLoading(true);

    try {
      let allCourseData = [];
      let lastHeaderPerCourse = {};
      let lastSectionPerCourse = {};
      let scenarioTextsPerCourse = {};

      selectedCourses.forEach((courseId) => {
        lastHeaderPerCourse[courseId] = `No header found for ${courseId}`;
        lastSectionPerCourse[courseId] = "";
        scenarioTextsPerCourse[courseId] = [];
      });

      const questionData = await fetchExamQuestions(examId, selectedCourses, token);
      allCourseData.push(...questionData);

      allCourseData.sort((a, b) => a.id - b.id);

      let finalExamQuestions = [];

      for (const q of allCourseData) {
        const cid = q.course_id;

        if (q.type === "H") {
          lastHeaderPerCourse[cid] = q.question;
        } else if (q.type === "S") {
          lastSectionPerCourse[cid] = q.question;
          scenarioTextsPerCourse[cid] = [];
        } else if (q.type === "Q") {
          scenarioTextsPerCourse[cid] = [];
          scenarioTextsPerCourse[cid].push(q.question);
        }

        finalExamQuestions.push(q);

        if (q.type === "T" || q.type === "FA") {
          questionSectionMap[q.id] = lastSectionPerCourse[cid] || "";
          scenarioMap[q.id] = [...scenarioTextsPerCourse[cid]];
        }
      }

      let headersMap = {};
      for (const cid of selectedCourses) {
        headersMap[cid] = lastHeaderPerCourse[cid];
      }

      setCourseHeaders(headersMap);
      setQuestionSectionMap(questionSectionMap);
      setScenarioMap(scenarioMap);

      finalExamQuestions.sort((a, b) => a.id - b.id);
      setQuestions(finalExamQuestions);
      setError(null);
    } catch (err) {
      console.error("Error fetching exam Qs:", err);
      setError("Failed to load questions.");
    } finally {
      if (!skipClearingState) setIsLoading(false);
    }
  };

  // ---------------------------------------------------------------------------
  // handleResumeExam (normal exam mode)
  // ---------------------------------------------------------------------------
  const handleResumeExam = async () => {
    try {
      console.log("⏯ Resuming exam from saved answers…");
      setFeedback(null);

      const fetchedAnswers = await fetchSessionAnswers(sessionId, user.userId, token);
      if (!fetchedAnswers || !Array.isArray(fetchedAnswers)) {
        console.warn("No saved answers returned.");
        return;
      }
      setExistingAnswers(fetchedAnswers);

      // Build the "answers" object from selected_answer
      const tempAnswers = {};
      for (const row of fetchedAnswers) {
        const isNumeric = /^\d+$/.test(row.selected_answer);
        const value = isNumeric ? parseInt(row.selected_answer, 10) : row.selected_answer;
        tempAnswers[row.question_id] = value;
      }
      setAnswers(tempAnswers);

      // Figure out last answered T/FA question
      const answeredIds = Object.keys(tempAnswers).map(Number);
      const answeredTFA = answeredIds.filter((id) => {
        const q = questions.find((qq) => qq.id === id);
        return q && (q.type === "T" || q.type === "FA");
      });

      if (answeredTFA.length === 0) {
        setPageIndex(0);
        return;
      }
      answeredTFA.sort((a, b) => a - b);
      const lastId = answeredTFA[answeredTFA.length - 1];

      const matchIndex = tfaIndexes.findIndex((fi) => questions[fi]?.id === lastId);
      setPageIndex(matchIndex >= 0 ? matchIndex : 0);
    } catch (err) {
      console.error("Error resuming exam:", err);
    }
  };

  // ---------------------------------------------------------------------------
  // handleAnswerSelection (first attempt)
  // ---------------------------------------------------------------------------
  const handleAnswerSelection = (questionId, selectedOptionIndex) => {
    setAnswers((prev) => ({ ...prev, [questionId]: selectedOptionIndex }));
    // If we’re in practice mode, provide immediate correctness feedback
    if (!isExamMode && currentQuestion?.type === "T") {
      const correctIdx = currentQuestion.right_ans;
      if (selectedOptionIndex === correctIdx) {
        setFeedback({ message: "Correct", type: "correct" });
      } else {
        setFeedback({ message: "Incorrect", type: "incorrect" });
      }
    }
  };

  // For free-answer, store text in answers
  const handleFreeAnswerSelection = (questionId, text) => {
    setAnswers((prev) => ({ ...prev, [questionId]: text }));
  };

  // ---------------------------------------------------------------------------
  // Save answer on next/prev in exam mode
  // ---------------------------------------------------------------------------
  const saveAnswer = async (masterIndex) => {
    if (!isExamMode) return;
    const q = questions[masterIndex];
    if (!q) return;
    const userAns = answers[q.id];
    if (!userAns) return;

    try {
      const exists = await checkAnswerExistsInDB(sessionId, q.id, user.userId, token);
      if (exists) {
        await updateUserAnswer(sessionId, q.id, user.userId, userAns, token);
      } else {
        await submitUserAnswer(sessionId, q.id, user.userId, userAns, token);
      }
    } catch (err) {
      console.error("Error saving/updating exam answer:", err);
    }
  };

  const handleNext = () => {
    saveAnswer(currentMasterIndex);
    if (pageIndex < tfaIndexes.length - 1) {
      setPageIndex((prev) => prev + 1);
      if (!isExamMode) setFeedback(null);
    }
  };

  const handlePrevious = () => {
    saveAnswer(currentMasterIndex);
    if (pageIndex > 0) {
      setPageIndex((prev) => prev - 1);
      if (!isExamMode) setFeedback(null);
    }
  };

  // ---------------------------------------------------------------------------
  // 14) Save all answers before final submit
  // ---------------------------------------------------------------------------
  const saveAllAnswers = async () => {
    for (let i = 0; i < questions.length; i++) {
      const q = questions[i];
      const userAns = answers[q.id];
      if (!userAns) continue;

      try {
        const exists = await checkAnswerExistsInDB(sessionId, q.id, user.userId, token);
        if (exists) {
          await updateUserAnswer(sessionId, q.id, user.userId, userAns, token);
        } else {
          await submitUserAnswer(sessionId, q.id, user.userId, userAns, token);
        }
      } catch (err) {
        console.error("Error saving final exam answer:", err);
      }
    }
  };

  // ---------------------------------------------------------------------------
  // 15) computeAndSetFinalResults
  // ---------------------------------------------------------------------------
  const computeAndSetFinalResults = async () => {
    let correctCount = 0;
    let totalTFA = 0;
    let courseStats = {};

    const questionDetails = questions.map((q) => {
      const cid = q.course_id;
      if (!courseStats[cid]) {
        courseStats[cid] = { correct: 0, total: 0 };
      }
      const wasInReview = reviewQuestions.some((rq) => rq.id === q.id);

      let firstAttemptAnswer = answers[q.id];
      let secondAttemptAnswer = wasInReview ? reviewAnswers[q.id] : null;
      let finalAnswer = null;
      let isCorrect = null;

      if (q.type === "T" || q.type === "FA") {
        courseStats[cid].total++;
        totalTFA++;
        if (q.type === "FA") {
          finalAnswer = secondAttemptAnswer || firstAttemptAnswer;
          // For demonstration, we mark free-answer as "correct"
          isCorrect = true;
        } else {
          finalAnswer = wasInReview
            ? secondAttemptAnswer || firstAttemptAnswer
            : firstAttemptAnswer;
          isCorrect = finalAnswer === q.right_ans;
        }
        if (isCorrect) {
          correctCount++;
          courseStats[cid].correct++;
        }
      }
      return {
        questionData: q,
        wasInReview,
        firstAttemptAnswer,
        secondAttemptAnswer,
        finalAnswer,
        isCorrect
      };
    });

    const score = totalTFA ? correctCount / totalTFA : 0;
    setFinalScore(score);
    setFinalResults(questionDetails);

    let perCourse = {};
    for (const cid of Object.keys(courseStats)) {
      const { correct, total } = courseStats[cid];
      const pct = total ? correct / total : 0;
      perCourse[cid] = pct;
    }
    setCourseScores(perCourse);

    // finalize session
    await updateExamSessionAsCompleted(sessionId, score, token);

    // snapshot answers & cleanup
    snapshotCompletedExams(
      examId,
      sessionId,
      user.userId,
      questionDetails,
      managerExplanations,
      scenarioMap,
      token
    );

    await fetchWithAuth(`/api/exam_answers/cleanup/${sessionId}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` }
    });

    // navigate to CompletedExams
    navigate("/dashboard/completed-exams", {
      state: { sessionId, userId: user.userId }
    });
  };

  // ---------------------------------------------------------------------------
  // 16) handleSubmitExam
  // ---------------------------------------------------------------------------
  const handleSubmitExam = async () => {
    if (isExamMode) {
      await saveAllAnswers();

      // quick calculation for first-attempt score
      let firstAttemptCorrect = 0;
      let totalTFA = 0;
      for (const q of questions) {
        if (q.type === "T" || q.type === "FA") {
          totalTFA++;
          if (q.type === "FA") {
            firstAttemptCorrect++;
          } else if (answers[q.id] === q.right_ans) {
            firstAttemptCorrect++;
          }
        }
      }
      const firstAttemptScore = totalTFA > 0 ? firstAttemptCorrect / totalTFA : 0;

      // If perfect or < 50%, finalize. Otherwise second attempt
      if (firstAttemptScore === 1 || firstAttemptScore < 0.5) {
        computeAndSetFinalResults();
      } else {
        await updateExamMode("Review");
        // Build the list of T-type questions answered incorrectly
        const existingIncorrectT = questions.filter(
          (qq) => qq.type === "T" && answers[qq.id] !== qq.right_ans
        );
        const incorrectWithPrev = existingIncorrectT.map((qq) => ({
          ...qq,
          previous_user_answer: answers[qq.id]
        }));
        setReviewQuestions(incorrectWithPrev);
        setIsReviewMode(true);
        setIsReviewStarted(false);
      }
      return;
    }

    // if we were in practice mode => move on to exam instructions
    setIsExamMode(true);
    setIsExamStarted(false);
    setQuestions([]);
    setAnswers({});
    setPageIndex(0);
  };

  // ---------------------------------------------------------------------------
  // 17) Switch from practice to exam instructions
  // ---------------------------------------------------------------------------
  const handleSwitchToExamInstructions = () => {
    setIsExamMode(true);
    setIsExamStarted(false);
    setQuestions([]);
    setAnswers({});
    setPageIndex(0);
  };

  // ---------------------------------------------------------------------------
  // 18) Start practice / exam
  // ---------------------------------------------------------------------------
  const updatePracticeSession = async () => {
    if (!sessionId) return;
    try {
      await fetchWithAuth(`/api/exam_sessions/start/${sessionId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        }
      });
    } catch (err) {
      console.error("Error updating practice session:", err);
    }
  };

  const updateExamMode = async (newMode) => {
    if (!sessionId) return;
    try {
      await fetchWithAuth(`/api/exam_sessions/mode/${sessionId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ mode: newMode })
      });
    } catch (err) {
      console.error("Error updating exam mode:", err);
    }
  };

  const handleStartPracticeExam = async () => {
    await updatePracticeSession();
    setIsExamStarted(true);
    loadPracticeQuestions();
  };

  const handleStartExam = async () => {
    await updateExamMode("Exam");
    setIsExamStarted(true);
    setIsExamMode(true);
    setFeedback(null);
    setQuestions([]);
    setAnswers({});
    setPageIndex(0);
    loadExamQuestions();
  };

  // ---------------------------------------------------------------------------
  // 19) Second Attempt (Review Mode)
  // ---------------------------------------------------------------------------
  // This is the big difference: we store second_answer for each T question
  const saveReviewAnswer = async (index) => {
    const q = reviewQuestions[index];
    if (!q) return;
    const userAns = reviewAnswers[q.id];
    if (!userAns) return;

    let isCorrect2nd = true;
    if (q.type === "T") {
      isCorrect2nd = userAns === q.right_ans;
    } else if (q.type === "FA") {
      isCorrect2nd = true;
    }

    try {
      // This calls your API (exam_answers/second_attempt) which stores "second_answer"
      await submitSecondAttemptAnswer(
        sessionId,
        q.id,
        user.userId,
        userAns,
        isCorrect2nd,
        token
      );
    } catch (err) {
      console.error("Error saving second attempt:", err);
    }
  };

  const handleReviewAnswerSelection = (questionId, selectedOptionIndex) => {
    setReviewAnswers((prev) => ({ ...prev, [questionId]: selectedOptionIndex }));
  };

  const handleReviewFreeAnswerSelection = (questionId, text) => {
    setReviewAnswers((prev) => ({ ...prev, [questionId]: text }));
  };

  const handleReviewNext = async () => {
    const q = reviewQuestions[reviewCurrentIndex];
    if (!reviewAnswers[q.id]) return;
    await saveReviewAnswer(reviewCurrentIndex);

    if (reviewCurrentIndex < reviewQuestions.length - 1) {
      setReviewCurrentIndex((prev) => prev + 1);
    }
  };

  const handleReviewPrevious = async () => {
    if (reviewCurrentIndex > 0) {
      await saveReviewAnswer(reviewCurrentIndex);
      setReviewCurrentIndex((prev) => prev - 1);
    }
  };

  const finishReview = async () => {
    await saveReviewAnswer(reviewCurrentIndex);
    setIsReviewMode(false);
    setIsReviewStarted(false);
    computeAndSetFinalResults();
  };

  // ---------------------------------------------------------------------------
  // 20) Manager Explanations
  // ---------------------------------------------------------------------------
  const handleManagerExplanationChange = (questionId, text) => {
    setManagerExplanations((prev) => ({
      ...prev,
      [questionId]: text
    }));
  };

  // ---------------------------------------------------------------------------
  // 21) "Previous answer was..."
  // ---------------------------------------------------------------------------
  const renderPreviousAnswerText = (question) => {
    if (!question?.previous_user_answer) return null;
    if (question.type !== "T") return null;

    const chosenIndex = question.previous_user_answer;
    const ansProperty = `ans${chosenIndex}`;
    const text = question[ansProperty] || "Unknown answer";
    return (
      <p className="previous-answer">
        <em>Your previous answer was: {text}</em>
      </p>
    );
  };

  // ---------------------------------------------------------------------------
  // 22) If we are in second attempt "Review Mode"
  // ---------------------------------------------------------------------------
  if (isReviewMode) {
    if (!isReviewStarted) {
      if (reviewQuestions.length === 0 && !isLoading) {
        return (
          <div className="exam-container">
            <h2>Review Mode</h2>
            <p>No questions to review. Possibly you already finished.</p>
          </div>
        );
      }
      return (
        <div className="exam-container">
          <h2>You are now in Review Mode</h2>
          <p>
            Some answers were incorrect. You now have a chance to review you answers.
            Only answers that were incorrect will be shown.
            1. Your orignal answer is shown
            2. Use this as before. When you have finished, click the 'Submit Reviewed Answers' button.
          </p>
          <div className="exam-buttons">
            <button
              onClick={() => {
                if (reviewQuestions.length === 0) {
                  console.log("No incorrect Qs to review, finalizing now.");
                  computeAndSetFinalResults();
                } else {
                  setIsReviewStarted(true);
                }
              }}
            >
              {reviewQuestions.length > 0 ? "Review Your Answers" : "Finalize"}
            </button>
          </div>
        </div>
      );
    }

    // If "reviewStarted" => second-attempt question UI
    const q = reviewQuestions[reviewCurrentIndex];
    if (!q) return <div className="exam-container">No question found.</div>;

    const totalReview = reviewQuestions.length;
    const isLastReviewQ = reviewCurrentIndex === totalReview - 1;

    return (
      <div className="exam-container exam-content">
        <h2>Review Mode In Progress</h2>
        <p>
          Question {reviewCurrentIndex + 1} / {totalReview}
        </p>
        <p>{q.question}</p>

        

        <ul className="answer-options">
          {q.type === "T" &&
            ["ans1", "ans2", "ans3", "ans4"].map((opt, i) => {
              if (!q[opt]) return null;
              const optionValue = i + 1;
              return (
                <li
                  key={i}
                  className={`answer-option ${reviewAnswers[q.id] === optionValue ? "selected" : ""}`}
                  onClick={() => handleReviewAnswerSelection(q.id, optionValue)}
                >
                  <label style={{ cursor: "pointer" }}>
                    <input
                      type="radio"
                      name={`review-q-${q.id}`}
                      value={optionValue}
                      readOnly
                      checked={reviewAnswers[q.id] === optionValue}
                    />
                    {q[opt]}
                  </label>
                </li>
              );
            })}
          {q.type === "FA" && (
            <textarea
              ref={textAreaRef}
              className="free-answer-input"
              value={reviewAnswers[q.id] || ""}
              onChange={(e) => handleReviewFreeAnswerSelection(q.id, e.target.value)}
              placeholder="Type your revised answer here..."
            />
          )}
        </ul>

        {renderPreviousAnswerText(q)}
        

        <div className="exam-controls">
          {/* Training Docs + Report Issue in Review Mode */}
        <div className="exam-navigation">
          <a
            href="https://www.dropbox.com/scl/fi/cf0t6yxq80u4y53zqan5b/Training-Docs-List.pdf?rlkey=59v632o7n621v7eef3bmhzjlc&dl=0"
            target="_blank"
            rel="noopener noreferrer"
            className="training-docs-button"
          >
            Training Docs
          </a>

          <button className="report-issue-button" onClick={openReportModal}>
            Report an Issue
          </button>
        </div>
          <button onClick={handleReviewPrevious} disabled={reviewCurrentIndex === 0}>
            Previous
          </button>

          {!isLastReviewQ && (
            <button
              className="next-btn"
              onClick={handleReviewNext}
              disabled={!reviewAnswers[q.id]}
            >
              Next
            </button>
          )}

          {isLastReviewQ && (
            <button
              className="submit-btn"
              onClick={finishReview}
              disabled={!reviewAnswers[q.id]}
            >
              Submit Reviewed Answers
            </button>
          )}
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // 23) If we have NOT started and are NOT in exam => Practice splash
  // ---------------------------------------------------------------------------
  if (!isExamStarted && !isExamMode) {
    return (
      <div className="exam-container">
        <h2>Welcome to Practice Mode</h2>
        <p className="exam-title">
          <strong>Exam:</strong> {examName}
        </p>
        <div className="selected-courses">
          <strong>Selected Courses:</strong>{" "}
          {selectedCourses.length > 0 ? selectedCourses.join(", ") : "None"}
        </div>
        <p>
        In this mode, you will get a chance to practice answering the questions that you will have to answer properly in the exam.
         As you read through the reference text that you can find on by pressing the Training Docs button, you can then answer the questions below and it will tell you if you have got it right, and therefore you have understood the course.
          Once you have done practicing, you can then press ‘Switch to Exam Mode’ button and you will have to answer the same exam questions.
        </p>
        {error && <p className="error-message">{error}</p>}
        <div className="exam-buttons">
          <button onClick={handleStartPracticeExam}>Start Practice Exam</button>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // 24) If not started but is exam mode => exam splash
  // ---------------------------------------------------------------------------
  if (!isExamStarted && isExamMode) {
    return (
      <div className="exam-container">
        <h2>You are now in Exam Mode</h2>
        <p>
          Once you submit your exam, you will be graded, and it will determine if you are competent and knowledgeable in this course. 
        </p>
        <div className="exam-buttons">
          <button onClick={handleStartExam}>Start Exam</button>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // 25) Otherwise, normal T/FA progression
  // ---------------------------------------------------------------------------
  const hasAnswerForCurrent = currentQuestion
    ? Boolean(answers[currentQuestion.id])
    : false;

  // For Issue modal: show the user’s current selected answer text
  const selectedVal = currentQuestion ? answers[currentQuestion.id] : null;
  const selectedAnswer =
    currentQuestion?.type === "T" && selectedVal
      ? currentQuestion[`ans${selectedVal}`]
      : selectedVal || "";

  const submitIssueReport = async () => {
    try {
      const response = await fetchWithAuth("/api/report_issues", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: user.userId,
          user_name: user.fullname,
          assigned_home: user.carehome,
          question_id: currentQuestion?.id,
          question_text: currentQuestion?.question,
          selected_answer: selectedAnswer,
          comment: issueDescription,
          exam_name: examName,
          exam_header: currentHeaderText
        })
      });
      if (!response.ok) {
        throw new Error(`Server responded with ${response.status}`);
      }
      console.log("Issue saved successfully!");
    } catch (error) {
      console.error("Failed to report issue:", error);
    } finally {
      closeReportModal();
    }
  };

  return (
    <div className="exam-container">
      <div className="exam-header-left">
        <div className="exam-header-row">
          <h3 className="exam-name">{examName}</h3>
          {isExamStarted && currentHeaderText && (
            <p className="header-question">{currentHeaderText}</p>
          )}
          {isExamStarted && currentSectionText && (
            <p className="section-question">Section: {currentSectionText}</p>
          )}
        </div>
      </div>

      <div className="exam-content">
        {scenarioTexts.length > 0 && (
          <div className="scenario-block">
            {scenarioTexts.map((txt, i) => (
              <p key={i} className="scenario-text">
                <strong>Scenario:</strong> {txt}
              </p>
            ))}
          </div>
        )}

        {currentQuestion?.explanation && (
          <div className="explanation-container">
            <div className="explanation">
              <strong>Explanation:</strong> {currentQuestion.explanation}
            </div>
          </div>
        )}

        {isLoading ? (
          <p>Loading questions...</p>
        ) : (
          <>
            {currentQuestion ? (
              <>
                <div className="question-container">
                  <p>
                    <strong>
                      Question {pageIndex + 1} / {tfaIndexes.length}
                    </strong>
                  </p>
                  <p>{currentQuestion.question}</p>
                </div>

                <div className="answers-container">
                  <ul className="answer-options">
                    {currentQuestion.type === "T" &&
                      ["ans1", "ans2", "ans3", "ans4"].map((opt, i) => {
                        if (!currentQuestion[opt]) return null;
                        const optionValue = i + 1;
                        return (
                          <li
                            key={i}
                            className={`answer-option ${answers[currentQuestion.id] === optionValue ? "selected" : ""}`}
                            onClick={() =>
                              handleAnswerSelection(currentQuestion.id, optionValue)
                            }
                          >
                            <label style={{ cursor: "pointer" }}>
                              <input
                                type="radio"
                                name={`question-${currentQuestion.id}`}
                                value={optionValue}
                                readOnly
                                checked={answers[currentQuestion.id] === optionValue}
                              />
                              {currentQuestion[opt]}
                            </label>
                          </li>
                        );
                      })}

                    {currentQuestion.type === "FA" && (
                      <textarea
                        ref={textAreaRef}
                        className="free-answer-input"
                        value={answers[currentQuestion.id] || ""}
                        onChange={(e) =>
                          handleFreeAnswerSelection(currentQuestion.id, e.target.value)
                        }
                        placeholder="Type your answer here..."
                      />
                    )}
                  </ul>

                  {/* If we're in practice mode, show immediate feedback */}
                  {!isExamMode && feedback && (
                    <div className="exam-feedback-container">
                      <p className={`exam-feedback ${feedback.type}`}>
                        {feedback.message}
                      </p>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <p>No T/FA question found.</p>
            )}

            <div
              className={`exam-controls ${
                pageIndex === tfaIndexes.length - 1 ? "last-question" : ""
              }`}
            >
              {/* If still in practice mode => show "Switch to Exam" button */}
              {!isExamMode && (
                <div className="exam-left">
                  <button onClick={handleSwitchToExamInstructions}>
                    I'm Ready. Start Exam
                  </button>
                </div>
              )}

              <div className="exam-navigation">
                <a
                  href="https://www.dropbox.com/scl/fi/cf0t6yxq80u4y53zqan5b/Training-Docs-List.pdf?rlkey=59v632o7n621v7eef3bmhzjlc&dl=0"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="training-docs-button"
                >
                  Training Docs
                </a>

                <button className="report-issue-button" onClick={openReportModal}>
                  Report an Issue
                </button>

                <button onClick={handlePrevious} disabled={pageIndex === 0}>
                  Previous
                </button>

                {pageIndex < tfaIndexes.length - 1 && (
                  <button
                    className="next-btn"
                    onClick={handleNext}
                    disabled={!hasAnswerForCurrent}
                  >
                    Next
                  </button>
                )}

                {pageIndex === tfaIndexes.length - 1 && (
                  <button
                    className="submit-btn"
                    onClick={handleSubmitExam}
                    disabled={!hasAnswerForCurrent}
                  >
                    {isExamMode ? "Submit Exam" : "Finish Practice Exam"}
                  </button>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Modal for reporting an issue */}
      {isReportModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>Report an Issue</h2>
            <p>
              <strong>Name:</strong> {user?.fullname}
            </p>
            <p>
              <strong>Care Home:</strong> {user?.carehome}
            </p>
            <p>
              <strong>Exam Name:</strong> {examName}
            </p>
            <p>
              <strong>Header:</strong> {currentHeaderText}
            </p>
            <p>
              <strong>Question:</strong> {currentQuestion?.question}
            </p>
            <p>
              <strong>Selected Answer:</strong> {selectedAnswer}
            </p>
            <textarea
              className="issue-textarea"
              placeholder="Describe the issue..."
              value={issueDescription}
              onChange={(e) => setIssueDescription(e.target.value)}
            />
            <div className="modal-buttons">
              <button onClick={submitIssueReport}>Submit</button>
              <button onClick={closeReportModal}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExamPage;
